import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import Iconify from "../../minimals/components/iconify";
import StickyTable from "../shared/StickyTable";
import DashboardPermissionDialog from "./DashboardPermissionDialog";
import { DashboardReportPermission } from "../types/app";

export default function DashboardPermissionsTable() {
  const [tableData, setTableData] = useState<DashboardReportPermission[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<DashboardReportPermission | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const headers = ["Report Type", "Department", "Team", "Role Type", "Actions"];

  const loadPermissions = async () => {
    try {
      const { data } = await axios.get<DashboardReportPermission[]>(
        `/api/clientphonebook/dashboardReportPermissions/getall`
      );

      const sortedData = [...data].sort((a, b) => {
        const reportNameA = a.dashboardReportType?.name || "";
        const reportNameB = b.dashboardReportType?.name || "";
        const reportCompare = reportNameA.localeCompare(reportNameB);

        if (reportCompare !== 0) return reportCompare;

        const teamNameA = a.internalUserTeam?.name || "";
        const teamNameB = b.internalUserTeam?.name || "";
        return teamNameA.localeCompare(teamNameB);
      });

      setTableData(sortedData);
    } catch (error) {
      enqueueSnackbar("Error loading permissions", { variant: "error" });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.post(`/api/clientphonebook/dashboardReportPermissions/DeleteReportPermissionRequest`, {
        id: id
      });
      enqueueSnackbar("Permission deleted successfully", { variant: "success" });
      loadPermissions();
    } catch (error) {
      enqueueSnackbar("Error deleting permission", { variant: "error" });
    }
  };

  const handleEdit = (permission: DashboardReportPermission) => {
    setEditingPermission(permission);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingPermission(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingPermission(null);
    loadPermissions();
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  const renderTableBody = () => {
    if (tableData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length} align="center">
            <Typography variant="subtitle2">No permissions found</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return tableData.map((row) => (
      <TableRow key={row.id}>
        <TableCell>{row.dashboardReportType?.name}</TableCell>
        <TableCell>{row.internalUserDepartment?.name || "All Departments"}</TableCell>
        <TableCell>{row.internalUserTeam?.name || "All Teams"}</TableCell>
        <TableCell>{row.internalUserRoleType?.name || "All Roles"}</TableCell>
        <TableCell>
          <IconButton onClick={() => handleEdit(row)}>
            <Iconify icon="mdi:lead-pencil" />
          </IconButton>
          <IconButton onClick={() => handleDelete(row.id)}>
            <Iconify icon="mdi:delete" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
        <Typography variant="h4">Dashboard Report Permissions</Typography>
        <Button variant="contained" onClick={handleAdd}>
          Add Permission
        </Button>
      </Box>

      <Card>
        <TableContainer sx={{ overflow: "unset" }}>
          <StickyTable sx={{ minWidth: 720, textAlign: "left" }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </StickyTable>
        </TableContainer>
        <Divider />
      </Card>

      <DashboardPermissionDialog open={dialogOpen} onClose={handleDialogClose} permission={editingPermission} />
    </>
  );
}
